import React, { useEffect, useState } from "react";
import Joi from "joi-browser";
import { useDispatch, useSelector } from "react-redux";
import { 
  getEmailTemplates, 
  getLetterTemplates, 
  loadFormsByType 
} from "../app/store/template";
import Select from "../comman/select";
import TextArea from "../comman/textarea";

const TemplatePopup = ({ handleSubmit, selectedTemplate, onClose, isLoading }) => {
  const dispatch = useDispatch();
  
  const emailTemplates = useSelector(getEmailTemplates) || [];
  const letterTemplates = useSelector(getLetterTemplates) || [];
  
  const [data, setData] = useState({
    templateType: "",
    templateId: "",
    notes: ""
  });
  
  const [errors, setErrors] = useState({});

  const TemplateTypes = [
    { id: "Email", name: "Email Template" }, 
    { id: "Letter", name: "Letter Template" }
  ];

  const schema = {
    templateType: Joi.string().required().label("Template Type"),
    templateId: Joi.string().required().label("Template"),
    notes: Joi.string().required().label("Notes"),
  };

  useEffect(() => {
    dispatch(loadFormsByType("EmailTemplate"));
    dispatch(loadFormsByType("LetterTemplate"));
  }, [dispatch]);

  useEffect(() => {
    if (selectedTemplate) {
      setData(selectedTemplate);
    } else {
      setData({
        templateType: "",
        templateId: "",
        notes: ""
      });
    }
  }, [selectedTemplate]);

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const subSchema = { [name]: schema[name] };
    const { error } = Joi.validate(obj, subSchema);
    return error ? error.details[0].message : null;
  };

  const handleChange = ({ currentTarget: input }) => {
    const newErrors = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) newErrors[input.name] = errorMessage;
    else delete newErrors[input.name];

    const newData = { ...data };
    newData[input.name] = input.value;
    
    // Reset templateId if type changes
    if (input.name === "templateType") {
      newData.templateId = "";
    }

    setData(newData);
    setErrors(newErrors);
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(data, schema, options);
    if (!error) return null;

    const newErrors = {};
    for (let item of error.details) newErrors[item.path[0]] = item.message;
    return newErrors;
  };

  const onSubmit = (e) => {
    if (e) e.preventDefault();
    const newErrors = validate();
    setErrors(newErrors || {});
    if (newErrors) return;

    const finalData = { ...data };
    const templates = data.templateType === "Letter" ? letterTemplates : emailTemplates;
    const selectedItem = templates.find(x => x.id === parseInt(data.templateId));
    
    if (selectedItem) {
      finalData.templateName = selectedItem.formTitle;
    }
    
    handleSubmit(finalData);
  };

  const isInvalid = validate() !== null;

  const richUiStyles = `
    :root {
      --primary-color: #2563eb;
      --primary-hover: #1d4ed8;
      --primary-soft: #eff6ff;
      --primary-glow: rgba(37, 99, 235, 0.12);
      --secondary-color: #64748b;
      --secondary-hover: #475569;
      --border-color: #e2e8f0;
      --bg-light: #f8fafc;
      --text-main: #0f172a;
      --text-muted: #64748b;
      --radius-lg: 16px;
      --radius-md: 12px;
      --radius-sm: 8px;
    }

    .template-popup-container {
      background: #ffffff;
      padding: 12px;
      border-radius: 12px;
      color: var(--text-main);
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }

    .form-section {
      margin-bottom: 24px;
      animation: fadeIn 0.5s ease-out;
    }

    .modern-label {
      font-size: 13.5px;
      font-weight: 700;
      color: var(--text-main);
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .modern-label i {
      color: var(--primary-color);
      font-size: 14px;
    }

    .wf-popup-footer {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-top: 32px;
      padding-top: 24px;
      border-top: 1.5px solid var(--border-color);
    }

    .wf-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px 24px;
      font-size: 14px;
      font-weight: 700;
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      border: 1.5px solid transparent;
      min-width: 130px;
      outline: none !important;
    }

    .wf-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .wf-btn-primary {
      background: var(--primary-color);
      color: #ffffff;
      border-color: var(--primary-color);
    }

    .wf-btn-primary:hover:not(:disabled) {
      background: var(--primary-hover);
      border-color: var(--primary-hover);
      transform: translateY(-1.5px);
      box-shadow: 0 8px 20px -4px rgba(37, 99, 235, 0.35);
    }

    .wf-btn-secondary {
      background: #fff;
      color: var(--secondary-color);
      border-color: var(--border-color);
    }

    .wf-btn-secondary:hover:not(:disabled) {
      background: var(--bg-light);
      color: var(--text-main);
      border-color: var(--secondary-hover);
      transform: translateY(-1.5px);
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  return (
    <div className="template-popup-container">
      <style>{richUiStyles}</style>
      <form onSubmit={onSubmit}>
        <div className="row">
          <div className="col-md-12 form-section">
            <label className="modern-label">
              <i className="fa fa-th-large"></i> Template Type
            </label>
            <Select
              name="templateType"
              value={data.templateType}
              options={TemplateTypes}
              onChange={handleChange}
              error={errors.templateType}
              optName="name"
              optValue="id"
              placeholder="Select Template Type"
            />
          </div>

          {data.templateType && (
            <div className="col-md-12 form-section">
              <label className="modern-label">
                <i className={`fa ${data.templateType === 'Email' ? 'fa-envelope' : 'fa-file-text'}`}></i>
                Select {data.templateType} Template
              </label>
              <Select
                name="templateId"
                value={data.templateId}
                options={data.templateType === "Letter" ? letterTemplates : emailTemplates}
                onChange={handleChange}
                error={errors.templateId}
                optName="formTitle"
                optValue="id"
                placeholder={`Choose ${data.templateType.toLowerCase()} template...`}
              />
            </div>
          )}

          <div className="col-lg-12 form-section">
            <label className="modern-label">
              <i className="fa fa-sticky-note"></i> Additional Notes
            </label>
            <TextArea
              name="notes"
              value={data.notes}
              onChange={handleChange}
              error={errors.notes}
              placeholder="Enter any relevant notes here..."
            />
          </div>
        </div>

        <div className="wf-popup-footer">
          {onClose && (
            <button
              className="wf-btn wf-btn-secondary"
              onClick={onClose}
              type="button"
              disabled={isLoading}
            >
              <i className="fa fa-arrow-left" aria-hidden="true" />
              Back
            </button>
          )}
          <button 
            type="submit" 
            className="wf-btn wf-btn-primary"
            disabled={isLoading || isInvalid}
          >
            {isLoading ? (
              <>
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-check-circle" aria-hidden="true"></i>
                Save Template
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TemplatePopup;
